// src/Components/ImportMarkers/ExcelAssignData.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

const DARK_BLUE = '#00008B';
const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

const BASE_TARGET_OPTIONS = [
  'ICON',
  'POSITION (LAT, LNG)',
  'VISIBILITY',
  'HOVER PHOTO',
  'HOVER TEXT',
  'INFO',
  'PHOTOS',
  'DOCUMENTS',
  'DESCRIPTION',
];

const INFO_OPTIONS = [
  'DATE',
  'LOCATION ID',
  'TITLE',
  'WEB SITE',
  'LOCATION ADDRESS',
  'CITY',
  'STATE',
  'POSTCODE/ZIP',
  'COUNTRY',
  'CLIENT NAME',
  'CATEGORY',
  'REGION',
  'CONTROLLER',
  'KEYWORDS',
  'SHOWCASE',
  'HILIGHT',
];

const VISIBILITY_OPTIONS = ['PUBLIC', 'PUBLIC FROM', 'RESTRICTED', 'PRIVATE'];

const TEXT_FIELDS = [
  'LOCATION ID',
  'TITLE',
  'WEB SITE',
  'LOCATION ADDRESS',
  'CITY',
  'STATE',
  'POSTCODE/ZIP',
  'COUNTRY',
  'CLIENT NAME',
  'CONTROLLER',
  'KEYWORDS',
];

const BOOLEAN_FIELDS = ['SHOWCASE', 'HILIGHT'];

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const getIconUrl = (iconObj) => {
  if (!iconObj) return null;
  return (
    iconObj.url ||
    iconObj.imageUrl ||
    iconObj.iconUrl ||
    iconObj.downloadURL ||
    iconObj.image ||
    iconObj.src ||
    iconObj.fileUrl ||
    (typeof iconObj.icon === 'string' ? iconObj.icon : null)
  );
};

const handleGlobalInvertedKeyDown = (e, makeUppercase) => {
  if (makeUppercase && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const keyLower = e.key.toLowerCase();
    const keyUpper = e.key.toUpperCase();
    if (keyLower !== keyUpper) {
      e.preventDefault();
      e.stopPropagation();
      const char = e.shiftKey ? keyLower : keyUpper;

      const input = e.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const val = input.value;
      const newVal = val.slice(0, start) + char + val.slice(end);

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      ).set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, newVal);
        const ev = new Event('input', { bubbles: true });
        input.dispatchEvent(ev);
      }

      setTimeout(() => {
        input.selectionStart = input.selectionEnd = start + 1;
      }, 0);
    }
  }
};

const handleGlobalInvertedPaste = (e, makeUppercase) => {
  if (makeUppercase) {
    e.preventDefault();
    e.stopPropagation();
    const paste = (e.clipboardData || window.clipboardData).getData('text').toUpperCase();

    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const val = input.value;
    const newVal = val.slice(0, start) + paste + val.slice(end);

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    ).set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, newVal);
      const ev = new Event('input', { bubbles: true });
      input.dispatchEvent(ev);
    }

    setTimeout(() => {
      input.selectionStart = input.selectionEnd = start + paste.length;
    }, 0);
  }
};

export default function ExcelAssignData({
  hasData,
  columns,
  savedMenus,
  categories,
  regions,
  globalCategory,
  setGlobalCategory,
  customRows,
  handleCustomRowNameChange,
  markerIcons,
  selectedCustomIds,
  setSelectedCustomIds,
  rowMappings,
  setRowMappings,
  rowSubMappings,
  setRowSubMappings,
  rowStaticValues,
  setRowStaticValues,
  defaultPublicFromDate,
  setIsMenuDirty,
  makeUppercase,
}) {
  const allProperties = [...customRows];

  const historicalNames = new Set();

  if (savedMenus) {
    savedMenus.forEach((menu) => {
      if (menu.mappings) {
        menu.mappings.forEach((m) => {
          if (m.property && m.property !== '-') historicalNames.add(m.property);
        });
      } else if (menu.customRows) {
        menu.customRows.forEach((cr) => {
          if (cr.name && cr.name !== '-') historicalNames.add(cr.name);
        });
      }
    });
  }

  if (columns) {
    columns.forEach((c) => {
      if (c && c !== '-') historicalNames.add(c);
    });
  }

  const seenNorms = new Set();
  const autocompleteOptions = [];

  Array.from(historicalNames).forEach((opt) => {
    const displayVal = makeUppercase ? opt.toUpperCase() : opt;
    const norm = normalizeLooseString(displayVal);
    if (!seenNorms.has(norm)) {
      seenNorms.add(norm);
      autocompleteOptions.push(displayVal);
    }
  });

  autocompleteOptions.sort((a, b) => a.localeCompare(b));

  const isAllSelected =
    allProperties.length > 0 && selectedCustomIds.length === allProperties.length;
  const isIndeterminate =
    selectedCustomIds.length > 0 && selectedCustomIds.length < allProperties.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCustomIds(allProperties.map((r) => r.id));
    } else {
      setSelectedCustomIds([]);
    }
  };

  const handleCheckboxToggle = (id) => {
    setSelectedCustomIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
    );
  };

  const handleMappingChange = (id, value) => {
    setRowMappings((prev) => {
      const next = { ...prev };
      if (value === '') {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });

    setRowSubMappings((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (setRowStaticValues) {
      setRowStaticValues((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }

    if (setIsMenuDirty) setIsMenuDirty(true);
  };

  const handleSubMappingChange = (id, value) => {
    setRowSubMappings((prev) => {
      const next = { ...prev };
      if (value === '') {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });

    if (setRowStaticValues) {
      setRowStaticValues((prev) => {
        const next = { ...prev };
        if (rowMappings[id] === 'VISIBILITY' && value === 'PUBLIC FROM') {
          next[id] = defaultPublicFromDate;
        } else {
          delete next[id];
        }
        return next;
      });
    }

    if (setIsMenuDirty) setIsMenuDirty(true);
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flexGrow: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderTop: '2px solid #1976d2',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
      }}>
      <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'auto' }}>
        <TableHead>
          <TableRow
            sx={{
              height: '28px',
              '& th': {
                backgroundColor: '#e3f2fd',
                fontWeight: 'bold',
                borderBottom: '2px solid #1976d2',
                py: '2px',
                ...cellStyle,
              },
            }}>
            <TableCell
              padding='checkbox'
              sx={{
                width: '64px',
                minWidth: '64px',
                maxWidth: '64px',
                textAlign: 'center',
              }}>
              <Checkbox
                size='small'
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
                sx={{ p: 0.5, '&.Mui-checked': { color: '#1976d2' } }}
              />
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Property Name
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Assigned To
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Selection
            </TableCell>
            <TableCell align='left' sx={{ width: '25%' }}>
              Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allProperties.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align='center' sx={{ py: 3 }}>
                <Typography
                  color='text.secondary'
                  variant='body2'
                  sx={{ fontStyle: 'italic' }}>
                  No properties configured in this template. Please click the "Add New"
                  icon to create a row.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            allProperties.map((row) => {
              const isSelected = selectedCustomIds.includes(row.id);
              const mappedTarget = rowMappings[row.id];

              return (
                <TableRow
                  key={row.id}
                  hover
                  selected={isSelected}
                  onClick={() => handleCheckboxToggle(row.id)}
                  sx={{
                    height: '34px',
                    cursor: 'pointer',
                    '& td': { py: '4px', ...cellStyle },
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&.Mui-selected': { backgroundColor: 'transparent !important' },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}>
                  <TableCell
                    padding='checkbox'
                    sx={{
                      width: '64px',
                      minWidth: '64px',
                      maxWidth: '64px',
                      textAlign: 'center',
                    }}>
                    <Checkbox
                      checked={isSelected}
                      size='small'
                      sx={{
                        pointerEvents: 'none',
                        p: 0.5,
                        '&.Mui-checked': { color: '#1976d2' },
                      }}
                    />
                  </TableCell>
                  <TableCell align='left'>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      options={autocompleteOptions}
                      slotProps={{ popper: { sx: { zIndex: 100020 } } }}
                      inputValue={row.name === '-' ? '' : row.name || ''}
                      onInputChange={(event, newInputValue) => {
                        handleCustomRowNameChange(row.id, newInputValue || '-');
                      }}
                      renderInput={(params) => {
                        const origOnKeyDown = params.inputProps.onKeyDown;
                        return (
                          <TextField
                            {...params}
                            size='small'
                            placeholder='Enter Property...'
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{
                              ...params.inputProps,
                              onKeyDown: (e) => {
                                handleGlobalInvertedKeyDown(e, makeUppercase);
                                if (!e.defaultPrevented && origOnKeyDown) {
                                  origOnKeyDown(e);
                                }
                              },
                              onPaste: (e) => handleGlobalInvertedPaste(e, makeUppercase),
                            }}
                            sx={{
                              backgroundColor: '#e3f2fd',
                              borderRadius: '4px',
                              '& .MuiOutlinedInput-root': {
                                padding: '0 4px',
                                minHeight: '26px',
                                height: '26px',
                                '& fieldset': {
                                  borderColor: '#90caf9',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#1976d2',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#1976d2',
                                },
                              },
                              '& .MuiInputBase-input': {
                                color: DARK_BLUE,
                                fontSize: '0.875rem',
                                py: '0px',
                                px: '4px',
                              },
                            }}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={
                        BASE_TARGET_OPTIONS.includes(rowMappings[row.id])
                          ? rowMappings[row.id]
                          : ''
                      }
                      onChange={(e) => handleMappingChange(row.id, e.target.value)}
                      displayEmpty
                      size='small'
                      fullWidth
                      sx={{
                        height: '26px',
                        backgroundColor: '#fff',
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                          py: '0px',
                          minHeight: '26px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                      MenuProps={{ style: { zIndex: 100020 } }}>
                      <MenuItem value=''>
                        <em style={{ color: '#999' }}>Select mapping...</em>
                      </MenuItem>
                      {BASE_TARGET_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                    {mappedTarget === 'INFO' ? (
                      <Select
                        value={
                          INFO_OPTIONS.includes(rowSubMappings[row.id])
                            ? rowSubMappings[row.id]
                            : ''
                        }
                        onChange={(e) => handleSubMappingChange(row.id, e.target.value)}
                        displayEmpty
                        size='small'
                        fullWidth
                        sx={{
                          height: '26px',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': {
                            py: '0px',
                            minHeight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        MenuProps={{ style: { zIndex: 100020 } }}>
                        <MenuItem value=''>
                          <em style={{ color: '#999' }}>Select Info field...</em>
                        </MenuItem>
                        {INFO_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : mappedTarget === 'ICON' ? (
                      <Select
                        value={
                          markerIcons?.some(
                            (i) => String(i.id) === String(rowSubMappings[row.id]),
                          )
                            ? String(rowSubMappings[row.id])
                            : ''
                        }
                        onChange={(e) => handleSubMappingChange(row.id, e.target.value)}
                        displayEmpty
                        size='small'
                        fullWidth
                        sx={{
                          height: '26px',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': {
                            py: '0px',
                            minHeight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        MenuProps={{ style: { zIndex: 100020 } }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em style={{ color: '#999' }}>Select Icon...</em>;
                          }
                          const selectedIcon = markerIcons?.find(
                            (icon) => String(icon.id) === String(selected),
                          );
                          if (!selectedIcon) return selected;

                          const iconImgUrl = getIconUrl(selectedIcon);

                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {iconImgUrl && (
                                <img
                                  src={iconImgUrl}
                                  alt={selectedIcon.name || 'icon'}
                                  style={{ width: 18, height: 18, objectFit: 'contain' }}
                                />
                              )}
                              <span>{selectedIcon.name || 'Unnamed Icon'}</span>
                            </Box>
                          );
                        }}>
                        <MenuItem value=''>
                          <em style={{ color: '#999' }}>Select Icon...</em>
                        </MenuItem>
                        {markerIcons &&
                          markerIcons.map((icon) => {
                            const iconImgUrl = getIconUrl(icon);

                            return (
                              <MenuItem
                                key={icon.id}
                                value={String(icon.id)}
                                sx={{
                                  fontSize: '0.875rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}>
                                {iconImgUrl && (
                                  <img
                                    src={iconImgUrl}
                                    alt={icon.name || 'icon'}
                                    style={{
                                      width: 18,
                                      height: 18,
                                      objectFit: 'contain',
                                    }}
                                  />
                                )}
                                {icon.name || 'Unnamed Icon'}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    ) : mappedTarget === 'VISIBILITY' ? (
                      <Select
                        value={
                          VISIBILITY_OPTIONS.includes(rowSubMappings[row.id])
                            ? rowSubMappings[row.id]
                            : ''
                        }
                        onChange={(e) => handleSubMappingChange(row.id, e.target.value)}
                        displayEmpty
                        size='small'
                        fullWidth
                        sx={{
                          height: '26px',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          '& .MuiSelect-select': {
                            py: '0px',
                            minHeight: '26px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                        MenuProps={{ style: { zIndex: 100020 } }}>
                        <MenuItem value=''>
                          <em style={{ color: '#999' }}>Select Visibility...</em>
                        </MenuItem>
                        {VISIBILITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography variant='caption' color='text.disabled'>
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                    {row.name !== '-' ? (
                      mappedTarget === 'INFO' ? (
                        rowSubMappings[row.id] === 'DATE' ? (
                          <TextField
                            type='date'
                            size='small'
                            value={rowStaticValues[row.id] || ''}
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                const val = makeUppercase
                                  ? e.target.value.toUpperCase()
                                  : e.target.value;
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: val,
                                }));
                              }
                              if (setIsMenuDirty) setIsMenuDirty(true);
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              '& .MuiInputBase-root': { height: '26px' },
                              '& .MuiInputBase-input': { py: '0px', px: '8px' },
                            }}
                          />
                        ) : rowSubMappings[row.id] === 'CATEGORY' ? (
                          <Select
                            size='small'
                            displayEmpty
                            value={
                              categories?.some(
                                (c) => String(c.id) === String(rowStaticValues[row.id]),
                              )
                                ? String(rowStaticValues[row.id])
                                : ''
                            }
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                const val = makeUppercase
                                  ? e.target.value.toUpperCase()
                                  : e.target.value;
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: val,
                                }));
                              }
                              if (setIsMenuDirty) setIsMenuDirty(true);
                            }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': {
                                py: '0px',
                                minHeight: '26px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                            MenuProps={{ style: { zIndex: 100020 } }}>
                            <MenuItem value='' disabled>
                              <em style={{ color: '#999' }}>Select Category...</em>
                            </MenuItem>
                            {categories &&
                              categories.map((cat) => (
                                <MenuItem
                                  key={cat.id}
                                  value={String(cat.id)}
                                  sx={{ fontSize: '0.875rem' }}>
                                  {cat.name}
                                </MenuItem>
                              ))}
                          </Select>
                        ) : rowSubMappings[row.id] === 'REGION' ? (
                          <Select
                            size='small'
                            displayEmpty
                            value={
                              regions?.some(
                                (r) => String(r.id) === String(rowStaticValues[row.id]),
                              )
                                ? String(rowStaticValues[row.id])
                                : ''
                            }
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                const val = makeUppercase
                                  ? e.target.value.toUpperCase()
                                  : e.target.value;
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: val,
                                }));
                              }
                              if (setIsMenuDirty) setIsMenuDirty(true);
                            }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              fontSize: '0.875rem',
                              '& .MuiSelect-select': {
                                py: '0px',
                                minHeight: '26px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                            MenuProps={{ style: { zIndex: 100020 } }}>
                            <MenuItem value='' disabled>
                              <em style={{ color: '#999' }}>Select Region...</em>
                            </MenuItem>
                            {regions &&
                              regions.map((reg) => (
                                <MenuItem
                                  key={reg.id}
                                  value={String(reg.id)}
                                  sx={{ fontSize: '0.875rem' }}>
                                  {reg.name}
                                </MenuItem>
                              ))}
                          </Select>
                        ) : BOOLEAN_FIELDS.includes(rowSubMappings[row.id]) ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: '26px',
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#fff',
                              px: 1,
                              borderRadius: '4px',
                            }}>
                            <Checkbox
                              size='small'
                              checked={
                                String(rowStaticValues[row.id]).trim().toLowerCase() ===
                                  'true' ||
                                String(rowStaticValues[row.id]).trim().toLowerCase() ===
                                  'yes' ||
                                String(rowStaticValues[row.id]).trim() === '1'
                              }
                              onChange={(e) => {
                                if (setRowStaticValues) {
                                  setRowStaticValues((prev) => ({
                                    ...prev,
                                    [row.id]: e.target.checked ? 'true' : 'false',
                                  }));
                                }
                                if (setIsMenuDirty) setIsMenuDirty(true);
                              }}
                              sx={{ py: 0 }}
                            />
                            <Typography variant='body2' sx={{ ml: 1, color: '#333' }}>
                              {rowSubMappings[row.id] === 'SHOWCASE'
                                ? 'Enable Showcase'
                                : 'Enable Hilight'}
                            </Typography>
                          </Box>
                        ) : TEXT_FIELDS.includes(rowSubMappings[row.id]) ? (
                          <TextField
                            size='small'
                            placeholder='Enter value'
                            value={rowStaticValues[row.id] || ''}
                            onChange={(e) => {
                              if (setRowStaticValues) {
                                setRowStaticValues((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }));
                              }
                              if (setIsMenuDirty) setIsMenuDirty(true);
                            }}
                            inputProps={{
                              onKeyDown: (e) =>
                                handleGlobalInvertedKeyDown(e, makeUppercase),
                              onPaste: (e) => handleGlobalInvertedPaste(e, makeUppercase),
                            }}
                            sx={{
                              width: '100%',
                              height: '26px',
                              backgroundColor: '#fff',
                              '& .MuiInputBase-root': { height: '26px' },
                              '& .MuiInputBase-input': {
                                py: '0px',
                                px: '8px',
                              },
                            }}
                          />
                        ) : null
                      ) : mappedTarget === 'VISIBILITY' &&
                        rowSubMappings[row.id] === 'PUBLIC FROM' ? (
                        <TextField
                          type='date'
                          size='small'
                          value={rowStaticValues[row.id] || ''}
                          onChange={(e) => {
                            if (setRowStaticValues) {
                              const val = makeUppercase
                                ? e.target.value.toUpperCase()
                                : e.target.value;
                              setRowStaticValues((prev) => ({
                                ...prev,
                                [row.id]: val,
                              }));
                            }
                            if (setIsMenuDirty) setIsMenuDirty(true);
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            width: '100%',
                            height: '26px',
                            backgroundColor: '#fff',
                            '& .MuiInputBase-root': { height: '26px' },
                            '& .MuiInputBase-input': { py: '0px', px: '8px' },
                          }}
                        />
                      ) : null
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
